import type { StairDirection, SymbolId } from './types';
import { DEFAULT_WALL_THICKNESS } from './canvasConstants';

/**
 * Resolution-independent symbol geometry.
 *
 * Each symbol is described as a set of stroke primitives in a local box of the
 * given width/height (pixels). The same primitives are rendered to the live
 * Konva canvas and to the SVG/PNG export, guaranteeing they look identical.
 */
export type SymbolPrimitive =
  | { k: 'line'; pts: number[] }
  | { k: 'rect'; x: number; y: number; w: number; h: number; r?: number; fill?: boolean }
  | { k: 'circle'; x: number; y: number; r: number }
  | { k: 'ellipse'; x: number; y: number; rx: number; ry: number }
  | { k: 'text'; x: number; y: number; text: string; size: number }
  /** Filled with the canvas colour so the wall stroke underneath an opening disappears. */
  | { k: 'hatch'; x: number; y: number; w: number; h: number };

function arcPolyline(cx: number, cy: number, r: number, startDeg: number, endDeg: number, segs = 24): number[] {
  const pts: number[] = [];
  const a0 = (startDeg * Math.PI) / 180;
  const a1 = (endDeg * Math.PI) / 180;
  for (let i = 0; i <= segs; i++) {
    const a = a0 + ((a1 - a0) * i) / segs;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return pts;
}

export interface SymbolDrawOptions {
  stairDirection?: StairDirection;
}

/**
 * Blank just thicker than the wall stroke so the opening is fully covered,
 * and exactly as wide as the door line so the leaf meets the wall with no gap.
 */
const WALL_COVER = DEFAULT_WALL_THICKNESS + 2;

/** `centerY` is the wall centreline in symbol-local pixels. */
function openingBlank(x: number, openingW: number, centerY: number): SymbolPrimitive {
  return { k: 'hatch', x, y: centerY - WALL_COVER / 2, w: openingW, h: WALL_COVER };
}

/** Hinged door. The wall centreline is the bottom of the box; the leaf starts on the room face of the blank. */
function swingDoor(openingW: number, boxH: number, leaves: 1 | 2): SymbolPrimitive[] {
  const centerY = boxH;
  const faceY = centerY - WALL_COVER / 2;
  const prims: SymbolPrimitive[] = [openingBlank(0, openingW, centerY)];
  if (leaves === 1) {
    prims.push(
      { k: 'line', pts: [0, faceY, 0, faceY - openingW] },
      { k: 'line', pts: arcPolyline(0, faceY, openingW, -90, 0) }
    );
  } else {
    const radius = openingW / 2;
    prims.push(
      { k: 'line', pts: [0, faceY, 0, faceY - radius] },
      { k: 'line', pts: arcPolyline(0, faceY, radius, -90, 0) },
      { k: 'line', pts: [openingW, faceY, openingW, faceY - radius] },
      { k: 'line', pts: arcPolyline(openingW, faceY, radius, 180, 270) }
    );
  }
  return prims;
}

export function getSymbolPrimitives(id: SymbolId, w: number, h: number, options?: SymbolDrawOptions): SymbolPrimitive[] {
  switch (id) {
    case 'door':
    case 'door-room':
    case 'door-cupboard':
      return swingDoor(w, h, 1);
    case 'double-door':
      return swingDoor(w, h, 2);
    case 'sliding-door': {
      return [
        openingBlank(0, w, h / 2),
        { k: 'rect', x: 0, y: 0, w, h },
        { k: 'line', pts: [w * 0.06, h * 0.2, w * 0.56, h * 0.2, w * 0.56, h * 0.8, w * 0.06, h * 0.8] },
        { k: 'line', pts: [w * 0.44, h * 0.2, w * 0.94, h * 0.2, w * 0.94, h * 0.8, w * 0.44, h * 0.8] },
      ];
    }
    case 'window': {
      return [
        openingBlank(0, w, h / 2),
        { k: 'rect', x: 0, y: 0, w, h },
        { k: 'line', pts: [0, h / 2, w, h / 2] },
      ];
    }
    case 'window-sliding': {
      return [
        openingBlank(0, w, h / 2),
        { k: 'rect', x: 0, y: 0, w, h },
        { k: 'line', pts: [w * 0.5, 0, w * 0.5, h] },
        { k: 'line', pts: [w * 0.06, h * 0.62, w * 0.44, h * 0.62] },
        { k: 'line', pts: [w * 0.56, h * 0.38, w * 0.94, h * 0.38] },
      ];
    }
    case 'window-bay': {
      return [
        { k: 'line', pts: [w * 0.08, h * 0.86, w * 0.92, h * 0.86] },
        { k: 'line', pts: [w * 0.08, h * 0.86, w * 0.26, h * 0.16, w * 0.74, h * 0.16, w * 0.92, h * 0.86] },
        { k: 'line', pts: [w * 0.18, h * 0.7, w * 0.32, h * 0.3, w * 0.68, h * 0.3, w * 0.82, h * 0.7] },
      ];
    }
    case 'stairs': {
      const prims: SymbolPrimitive[] = [{ k: 'rect', x: 0, y: 0, w, h }];
      const steps = Math.max(4, Math.round(h / Math.max(18, h / 12)));
      for (let i = 1; i < steps; i++) {
        const y = (h * i) / steps;
        prims.push({ k: 'line', pts: [0, y, w, y] });
      }
      const cx = w / 2;
      const size = Math.min(w * 0.28, 16);
      const down = options?.stairDirection === 'dn';
      if (down) {
        prims.push({ k: 'line', pts: [cx, h * 0.14, cx, h * 0.82] });
        prims.push({ k: 'line', pts: [cx - w * 0.14, h * 0.66, cx, h * 0.82] });
        prims.push({ k: 'line', pts: [cx + w * 0.14, h * 0.66, cx, h * 0.82] });
        prims.push({ k: 'text', x: cx, y: h * 0.08, text: 'DN', size });
      } else {
        prims.push({ k: 'line', pts: [cx, h * 0.86, cx, h * 0.18] });
        prims.push({ k: 'line', pts: [cx - w * 0.14, h * 0.32, cx, h * 0.18] });
        prims.push({ k: 'line', pts: [cx + w * 0.14, h * 0.32, cx, h * 0.18] });
        prims.push({ k: 'text', x: cx, y: h * 0.94, text: 'UP', size });
      }
      return prims;
    }
    case 'stairs-l': {
      const leg = w * 0.46;
      const land = h * 0.4;
      const prims: SymbolPrimitive[] = [
        { k: 'line', pts: [0, 0, leg, 0, leg, h - land, w, h - land, w, h, 0, h, 0, 0] },
      ];
      const vSteps = 6;
      for (let i = 1; i < vSteps; i++) {
        const y = ((h - land) * i) / vSteps;
        prims.push({ k: 'line', pts: [0, y, leg, y] });
      }
      const hSteps = 4;
      for (let i = 1; i < hSteps; i++) {
        const x = leg + ((w - leg) * i) / hSteps;
        prims.push({ k: 'line', pts: [x, h - land, x, h] });
      }
      const down = options?.stairDirection === 'dn';
      const cx = leg * 0.5;
      const size = Math.min(leg * 0.32, 14);
      if (down) {
        prims.push({ k: 'line', pts: [cx, h * 0.08, cx, h - land - leg * 0.08] });
        prims.push({ k: 'line', pts: [cx - leg * 0.16, h - land - leg * 0.28, cx, h - land - leg * 0.08] });
        prims.push({ k: 'line', pts: [cx + leg * 0.16, h - land - leg * 0.28, cx, h - land - leg * 0.08] });
      } else {
        prims.push({ k: 'line', pts: [cx, h - land - leg * 0.08, cx, h * 0.1] });
        prims.push({ k: 'line', pts: [cx - leg * 0.16, h * 0.26, cx, h * 0.1] });
        prims.push({ k: 'line', pts: [cx + leg * 0.16, h * 0.26, cx, h * 0.1] });
      }
      prims.push({ k: 'text', x: leg + (w - leg) / 2, y: h - land * 0.45, text: down ? 'DN' : 'UP', size });
      return prims;
    }
    case 'bed-double':
    case 'bed-single': {
      const single = id === 'bed-single';
      const prims: SymbolPrimitive[] = [{ k: 'rect', x: 0, y: 0, w, h, r: Math.min(w, h) * 0.04 }];
      // Duvet fold line.
      prims.push({ k: 'line', pts: [0, h * 0.3, w, h * 0.3] });
      const py0 = h * 0.05;
      const py1 = h * 0.24;
      if (single) {
        prims.push({ k: 'rect', x: w * 0.18, y: py0, w: w * 0.64, h: py1 - py0, r: 4 });
      } else {
        prims.push({ k: 'rect', x: w * 0.08, y: py0, w: w * 0.37, h: py1 - py0, r: 4 });
        prims.push({ k: 'rect', x: w * 0.55, y: py0, w: w * 0.37, h: py1 - py0, r: 4 });
      }
      return prims;
    }
    case 'sofa': {
      const arm = w * 0.12;
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: Math.min(w, h) * 0.08 },
        { k: 'line', pts: [arm, h * 0.32, w - arm, h * 0.32] },
        { k: 'line', pts: [arm, h * 0.32, arm, h] },
        { k: 'line', pts: [w - arm, h * 0.32, w - arm, h] },
        { k: 'line', pts: [w / 2, h * 0.32, w / 2, h] },
      ];
    }
    case 'sofa-two': {
      const arm = w * 0.14;
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: Math.min(w, h) * 0.08 },
        { k: 'line', pts: [arm, h * 0.32, w - arm, h * 0.32] },
        { k: 'line', pts: [arm, h * 0.32, arm, h] },
        { k: 'line', pts: [w - arm, h * 0.32, w - arm, h] },
      ];
    }
    case 'sofa-l': {
      const t = Math.min(w, h) * 0.42;
      return [
        { k: 'line', pts: [0, 0, w, 0, w, t, t, t, t, h, 0, h, 0, 0] },
        { k: 'line', pts: [t * 0.38, t * 0.42, w - t * 0.16, t * 0.42] },
        { k: 'line', pts: [t * 0.38, t, t * 0.38, h - t * 0.12] },
      ];
    }
    case 'kitchen-sink': {
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: 2 },
        { k: 'rect', x: w * 0.06, y: h * 0.18, w: w * 0.48, h: h * 0.68, r: 3 },
        { k: 'line', pts: [w * 0.62, h * 0.32, w * 0.94, h * 0.32] },
        { k: 'line', pts: [w * 0.62, h * 0.5, w * 0.94, h * 0.5] },
        { k: 'line', pts: [w * 0.62, h * 0.68, w * 0.94, h * 0.68] },
        { k: 'circle', x: w * 0.3, y: h * 0.1, r: Math.min(w, h) * 0.05 },
      ];
    }
    case 'kitchen-sink-double': {
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: 2 },
        { k: 'rect', x: w * 0.06, y: h * 0.22, w: w * 0.4, h: h * 0.66, r: 3 },
        { k: 'rect', x: w * 0.54, y: h * 0.22, w: w * 0.4, h: h * 0.66, r: 3 },
        { k: 'circle', x: w * 0.5, y: h * 0.1, r: Math.min(w, h) * 0.045 },
      ];
    }
    case 'range':
    case 'range-wide': {
      const cols = id === 'range-wide' ? 3 : 2;
      const rows = 2;
      const prims: SymbolPrimitive[] = [
        { k: 'rect', x: 0, y: 0, w, h, r: 2 },
        { k: 'rect', x: w * 0.05, y: h * 0.05, w: w * 0.9, h: h * 0.9 },
      ];
      const r = Math.min(w / cols, h / rows) * 0.28;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          prims.push({
            k: 'circle',
            x: (w * (col + 0.5)) / cols,
            y: (h * (row + 0.5)) / rows,
            r,
          });
        }
      }
      return prims;
    }
    case 'sink': {
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: 4 },
        { k: 'ellipse', x: w / 2, y: h * 0.58, rx: w * 0.34, ry: h * 0.3 },
        { k: 'circle', x: w / 2, y: h * 0.16, r: Math.min(w, h) * 0.06 },
      ];
    }
    case 'sink-double': {
      const r = Math.min(w, h) * 0.05;
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: 4 },
        { k: 'ellipse', x: w * 0.28, y: h * 0.6, rx: w * 0.16, ry: h * 0.26 },
        { k: 'ellipse', x: w * 0.72, y: h * 0.6, rx: w * 0.16, ry: h * 0.26 },
        { k: 'circle', x: w * 0.28, y: h * 0.16, r },
        { k: 'circle', x: w * 0.72, y: h * 0.16, r },
      ];
    }
    case 'toilet': {
      return [
        { k: 'rect', x: w * 0.15, y: 0, w: w * 0.7, h: h * 0.24, r: 3 },
        { k: 'ellipse', x: w / 2, y: h * 0.62, rx: w * 0.42, ry: h * 0.34 },
      ];
    }
    case 'toilet-wall': {
      return [
        { k: 'line', pts: [0, h * 0.08, w, h * 0.08] },
        { k: 'ellipse', x: w / 2, y: h * 0.58, rx: w * 0.4, ry: h * 0.3 },
        { k: 'line', pts: [w * 0.28, h * 0.34, w * 0.72, h * 0.34] },
      ];
    }
    case 'bathtub': {
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: Math.min(w, h) * 0.18 },
        { k: 'rect', x: w * 0.06, y: h * 0.16, w: w * 0.74, h: h * 0.68, r: Math.min(w, h) * 0.14 },
        { k: 'circle', x: w * 0.9, y: h * 0.5, r: Math.min(w, h) * 0.06 },
      ];
    }
    case 'bathtub-shower': {
      return [
        { k: 'rect', x: 0, y: 0, w, h, r: Math.min(w, h) * 0.18 },
        { k: 'rect', x: w * 0.06, y: h * 0.16, w: w * 0.62, h: h * 0.68, r: Math.min(w, h) * 0.14 },
        { k: 'circle', x: w * 0.9, y: h * 0.5, r: Math.min(w, h) * 0.06 },
        { k: 'line', pts: [w * 0.72, 0, w * 0.72, h] },
      ];
    }
    case 'corner-shower': {
      // Two walls meeting in the corner, curved glass screen, drain.
      const r = Math.min(w, h);
      return [
        { k: 'line', pts: [0, 0, 0, h] },
        { k: 'line', pts: [0, h, w, h] },
        { k: 'line', pts: arcPolyline(0, h, r, -90, 0) },
        { k: 'circle', x: r * 0.32, y: h - r * 0.32, r: Math.min(w, h) * 0.06 },
      ];
    }
    case 'stall-shower': {
      const gap = w * 0.42;
      const gx = (w - gap) / 2;
      return [
        { k: 'line', pts: [0, 0, w, 0] },
        { k: 'line', pts: [w, 0, w, h] },
        { k: 'line', pts: [w, h, gx + gap, h] },
        { k: 'line', pts: [gx, h, 0, h] },
        { k: 'line', pts: [0, h, 0, 0] },
        { k: 'circle', x: w / 2, y: h * 0.46, r: Math.min(w, h) * 0.06 },
        { k: 'circle', x: w / 2, y: h * 0.14, r: Math.min(w, h) * 0.04 },
        { k: 'line', pts: [w * 0.36, h * 0.22, w * 0.64, h * 0.22] },
      ];
    }
    case 'north':
    case 'north-point': {
      const cx = w / 2;
      const top = h * 0.24;
      const bot = h * 0.92;
      const hw = w * 0.34;
      return [
        { k: 'text', x: cx, y: h * 0.1, text: 'N', size: Math.min(h * 0.16, 14) },
        { k: 'line', pts: [cx, top, cx - hw, bot, cx + hw, bot, cx, top] },
        { k: 'line', pts: [cx, top, cx, bot] },
      ];
    }
    case 'north-arrow': {
      const cx = w / 2;
      const tip = h * 0.26;
      const tail = h * 0.92;
      const head = Math.min(w, h) * 0.22;
      return [
        { k: 'text', x: cx, y: h * 0.12, text: 'N', size: Math.min(h * 0.2, 16) },
        { k: 'line', pts: [cx, tip, cx, tail] },
        { k: 'line', pts: [cx - head, tip + head, cx, tip] },
        { k: 'line', pts: [cx + head, tip + head, cx, tip] },
      ];
    }
    case 'north-circle': {
      const cx = w / 2;
      const cy = h * 0.58;
      const r = Math.min(w, h) * 0.34;
      const tip = cy - r * 0.62;
      const base = cy + r * 0.38;
      const hw = r * 0.5;
      return [
        { k: 'text', x: cx, y: h * 0.12, text: 'N', size: Math.min(h * 0.18, 14) },
        { k: 'circle', x: cx, y: cy, r },
        { k: 'line', pts: [cx, tip, cx - hw, base, cx + hw, base, cx, tip] },
      ];
    }
    case 'north-ns': {
      const cx = w / 2;
      const tip = h * 0.2;
      const tail = h * 0.7;
      const head = Math.min(w, h) * 0.16;
      return [
        { k: 'text', x: cx, y: h * 0.1, text: 'N', size: Math.min(h * 0.16, 14) },
        { k: 'text', x: cx, y: h * 0.9, text: 'S', size: Math.min(h * 0.16, 14) },
        { k: 'line', pts: [cx, tip, cx, tail] },
        { k: 'line', pts: [cx - head, tip + head, cx, tip] },
        { k: 'line', pts: [cx + head, tip + head, cx, tip] },
      ];
    }
    case 'north-compass': {
      const cx = w / 2;
      const cy = h / 2;
      const r = Math.min(w, h) * 0.42;
      const a = w * 0.12;
      return [
        { k: 'circle', x: cx, y: cy, r },
        { k: 'line', pts: [cx, cy + r * 0.72, cx, cy - r * 0.72] },
        { k: 'line', pts: [cx - r * 0.6, cy, cx + r * 0.6, cy] },
        { k: 'line', pts: [cx - a, cy - r * 0.4, cx, cy - r * 0.72] },
        { k: 'line', pts: [cx + a, cy - r * 0.4, cx, cy - r * 0.72] },
        { k: 'text', x: cx, y: h * 0.06, text: 'N', size: Math.min(h * 0.2, 16) },
      ];
    }
    default:
      return [{ k: 'rect', x: 0, y: 0, w, h }];
  }
}
